import { getCookie } from 'cookies-next/client';

import { AuthProvider } from '@/utils/auth/persistedAuthProvider';

/**
 * Cross-provider auth discriminator cookie.
 *
 * Stores the active `AuthProvider` value so middleware, SSR, and client
 * hooks can pick the right session reader (Auth0 / Kinde / FHIR / Keycloak)
 * without probing each SDK.
 *
 * Lives at the auth aggregate root because the value can be set by any of
 * the four providers — do not move into a provider-specific folder.
 */
export const AUTH_PROVIDER_COOKIE = 'heidi-auth-provider';

/**
 * Reads and parses `AUTH_PROVIDER_COOKIE`, returning `null` for an unset or
 * unrecognised value. Headless — reads only the cookie, never `@/store` —
 * so callers that must not statically couple to the store (the catch-all
 * HTTP fallback, any module the store transitively reaches back through)
 * can read the cookie without pulling Redux in behind it.
 */
export function readProviderCookie(): AuthProvider | null {
  const raw = getCookie(AUTH_PROVIDER_COOKIE);
  if (raw === AuthProvider.AUTH0) return AuthProvider.AUTH0;
  if (raw === AuthProvider.KINDE) return AuthProvider.KINDE;
  if (raw === AuthProvider.KEYCLOAK) return AuthProvider.KEYCLOAK;
  if (raw === AuthProvider.FHIR) return AuthProvider.FHIR;
  if (raw === AuthProvider.AUTH0_DESKTOP) return AuthProvider.AUTH0_DESKTOP;
  return null;
}

/**
 * `SameSite` value for all auth cookies.
 *
 * `None` so cookies are sent inside a cross-site iframe — Heidi is embedded in
 * EHRs via `/integration/openapi/launch`. With `SameSite=Lax`, those embedded
 * requests carry no cookies, the in-frame session check (`/api/auth0-session`)
 * resolves to `isAuthenticated:false`, and the reactive forced-logout in
 * `useAuth0Session` fires a logout redirect — a refresh loop inside the iframe.
 *
 * The cookie spec requires `SameSite=None` to be paired with `Secure`, so every
 * auth cookie below is also `secure: true`. Chrome accepts `Secure` cookies on
 * `http://localhost`, so local dev still works without HTTPS.
 */
export const AUTH_COOKIE_SAME_SITE = 'none' as const;

const clientAuth0CookieDomain = process.env.NEXT_PUBLIC_AUTH0_COOKIE_DOMAIN;
const serverAuth0CookieDomain = process.env.AUTH0_COOKIE_DOMAIN;

/**
 * Client-side cookie identity tuple (path + optional domain). The browser
 * can only see `NEXT_PUBLIC_*` env vars, so client setters/deleters must use
 * the public mirror of the Auth0 cookie domain.
 */
export const authProviderCookieIdentity = {
  path: '/',
  ...(clientAuth0CookieDomain && { domain: clientAuth0CookieDomain }),
} as const;

/**
 * Server-side cookie identity tuple (path + optional domain). Server
 * response cookies should mirror the Auth0 SDK cookie domain exactly.
 */
export const serverAuthProviderCookieIdentity = {
  path: '/',
  ...(serverAuth0CookieDomain && { domain: serverAuth0CookieDomain }),
} as const;

/**
 * Full attributes for `AUTH_PROVIDER_COOKIE`. Client-readable
 * (`httpOnly: false`) so provider-aware client code — including
 * `getEffectiveAuthProvider()` and its callers (`useAuth0Session`,
 * `useIsAuthenticated`, the token-cache dispatcher, the scheduler)
 * — can read the active provider from JavaScript.
 */
export const authProviderCookieAttrs = {
  ...authProviderCookieIdentity,
  httpOnly: false, // client-side JavaScript can read it. This is intentional, but it means it must not contain secrets.
  // SameSite=None requires Secure. Chrome accepts Secure on http://localhost.
  secure: true,
  // None so the cookie is sent in a cross-site iframe (EHR embedding).
  sameSite: AUTH_COOKIE_SAME_SITE,
} as const;

/**
 * Full server-side attributes for `AUTH_PROVIDER_COOKIE`.
 */
export const serverAuthProviderCookieAttrs = {
  ...serverAuthProviderCookieIdentity,
  httpOnly: false,
  secure: true,
  sameSite: AUTH_COOKIE_SAME_SITE,
} as const;

/**
 * Delete-identity for the historical **host-only** `AUTH_PROVIDER_COOKIE`
 * write. Predates the domain-scoped identity above; the loop-guard in
 * `useSessionInvalidationTelemetry` deletes both to cover users whose
 * cookie still carries the older shape.
 *
 * Cookies-next requires the delete call to match the exact
 * (name, domain, path, sameSite, secure) tuple of the write. The
 * cookie is SET with `sameSite: 'none', secure: true`
 * (see `authProviderCookieAttrs`); omitting these on the delete
 * causes Chrome to reject the expired-cookie write in third-party
 * iframe contexts (EHR embeds), leaving the cookie in place and
 * breaking the forced-logout refresh-loop guard exactly where it's
 * needed. Carry the same attrs on the delete so the two operations
 * stay symmetric.
 */
export const hostOnlyAuthProviderCookieDeleteIdentity = {
  path: '/',
  sameSite: AUTH_COOKIE_SAME_SITE,
  secure: true,
} as const;

/**
 * Delete-identity for the current domain-scoped `AUTH_PROVIDER_COOKIE`
 * write. Derived from the same identity + `sameSite`/`secure` values
 * used by the set attributes above so the two can't drift.
 */
export const authProviderCookieDeleteIdentity = {
  ...authProviderCookieIdentity,
  sameSite: AUTH_COOKIE_SAME_SITE,
  secure: true,
} as const;
